/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/string/GetStringPositionBetween.ts
 * @create:      2025-06-26 11:17:01.851
 * @modify:      2025-07-11 21:19:12.827
 * @version:     6.0.0
 * @times:       10
 * @lines:       100
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { StringPosition } from "../class/StringPosition";
import { GetSubstring } from "./GetSubstring";

/**
 * Finds all substrings in the given string that are located between the specified start and end delimiters.
 *
 * @param str - The source string to search within.
 * @param start - The starting delimiter string.
 * @param end - The ending delimiter string.
 * @param max - The maximum number of matches to find. If set to -1, all matches are returned. Defaults to -1.
 * @param startIndex - The index in the source string to start searching from. Defaults to 0.
 * @returns An array of `StringPosition` objects representing the positions of substrings found between the delimiters.
 *
 * @remarks
 * - The function searches for non-overlapping occurrences of substrings between `start` and `end`.
 * - Each `StringPosition` contains the start and end indices of the found substring, as well as the substring itself in the `RelatedString` property.
 * - If either delimiter is not found, or if the input parameters are invalid, an empty array is returned.
 * 
 * @since 1.1.7
 * @version 2025-06-26
 */
export function GetStringPositionBetween(str: string, start: string, end: string, max = -1, startIndex = 0): StringPosition[] {
    const list: StringPosition[] = [];

    if (
        str &&
        start &&
        end &&
        str.length > 0 &&
        start.length > 0 &&
        end.length > 0
    ) {
        let current = startIndex;
        while (true) {
            const index = str.indexOf(start, current);
            if (index !== -1) {
                const endIndex = str.indexOf(
                    end,
                    index + start.length + 1,
                );
                if (endIndex !== -1) {
                    const pos = new StringPosition(
                        index + start.length,
                        endIndex,
                    );
                    pos.RelatedString = GetSubstring(str, pos);
                    list.push(
                        // str.substring(index + strStart.length, endIndex)
                        pos,
                    );

                    if (max > 0 && list.length >= max) {
                        break;
                    }

                    current = endIndex + end.length;
                } else {
                    break;
                }
            } else {
                break;
            }
        }
    }

    return list;
}
